import React, { useState, useEffect, useRef } from 'react';
import './ContactUs.css';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en';
import emailjs from '@emailjs/browser';

const ContactUs = () => {
  const [selectedCountry, setSelectedCountry] = useState('+1'); // Default to +1 for USA
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNo: '',
    message: ''
  });
  const [isDark, setIsDark] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'
  const [statusMessage, setStatusMessage] = useState('');
  
  const formRef = useRef();

  useEffect(() => {
    // Check initial theme
    setIsDark(document.documentElement.getAttribute('data-theme') === 'dark');

    // Listen for theme changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'data-theme') {
          setIsDark(document.documentElement.getAttribute('data-theme') === 'dark');
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  const handleCountryChange = (event) => {
    setSelectedCountry(event.target.value);
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleFocus = (fieldId) => {
    setFocusedField(fieldId);
  };

  const handleBlur = () => {
    setFocusedField(null);
  };

  const validateForm = () => {
    const { fullName, email, phoneNo, message } = formData;
    
    if (!fullName.trim()) {
      setStatusMessage('Please enter your full name');
      return false;
    }
    
    if (!email.trim()) {
      setStatusMessage('Please enter your email address');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setStatusMessage('Please enter a valid email address');
      return false;
    }
    
    if (!phoneNo.trim()) {
      setStatusMessage('Please enter your phone number');
      return false;
    }
    
    if (!message.trim()) {
      setStatusMessage('Please enter your message');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setStatusMessage('');

    try {
      // Replace these with your actual EmailJS credentials
      const result = await emailjs.sendForm(
        'service_9368f1h', // Replace with your EmailJS service ID
        'template_2hv2u5y', // Replace with your EmailJS template ID
        formRef.current,
        'daNmBdkU2GmolXv9e' // Replace with your EmailJS public key
      );

      if (result.status === 200) {
        setSubmitStatus('success');
        setStatusMessage('Thank you! Your message has been sent successfully.');
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phoneNo: '',
          message: ''
        });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('EmailJS Error:', error);
      setSubmitStatus('error');
      setStatusMessage('Sorry, there was an error sending your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearStatus = () => {
    setSubmitStatus(null);
    setStatusMessage('');
  };

  return (
    <div className="contact-us-container">
      <section className={`contact-us-hero ${isDark ? 'dark-mode' : ''}`}>
        <div className="contact-us-left">
          <h1 className="contact-us-title">Contact Us</h1>
          <p className="contact-us-description">
            Have questions or need support?<br />
            We're here to help — reach out anytime.
          </p>
          <div className="contact-info">
            <p className="contact-item"><span className="icon">✉️</span> genixai@info.com</p>
            <p className="contact-item"><span className="icon">📞</span> Support : (+91) 123 456 7890</p>
          </div>
        </div>
        <div className="contact-us-right">
          <div className="contact-form-card">
            <h2 className="form-card-title">We'd love to hear from you!</h2>
            <p className="form-card-subtitle">Let's get in touch</p>
            
            {/* Status Messages */}
            {submitStatus && (
              <div className={`status-message ${submitStatus}`}>
                <span>{statusMessage}</span>
                <button onClick={clearStatus} className="close-status">×</button>
              </div>
            )}
            
            <form ref={formRef} className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <div className="input-container">
                  <input 
                    type="text" 
                    name="fullName"
                    id="fullName" 
                    value={formData.fullName}
                    onChange={handleInputChange}
                    onFocus={() => handleFocus('fullName')}
                    onBlur={handleBlur}
                    placeholder={focusedField === 'fullName' ? '' : ''}
                    disabled={isSubmitting}
                  />
                  <label htmlFor="fullName">Full Name<span className="required">*</span></label>
                </div>
              </div>
              <div className="form-group">
                <div className="input-container">
                  <input 
                    type="email" 
                    name="email"
                    id="email" 
                    value={formData.email}
                    onChange={handleInputChange}
                    onFocus={() => handleFocus('email')}
                    onBlur={handleBlur}
                    placeholder={focusedField === 'email' ? '' : ''}
                    disabled={isSubmitting}
                  />
                  <label htmlFor="email">E-mail<span className="required">*</span></label>
                </div>
              </div>
              <div className="form-group phone-group">
                <div className="country-code-select-container">
                  <div className="input-container">
                    <select 
                      id="countryCode" 
                      value={selectedCountry} 
                      onChange={handleCountryChange}
                      disabled={isSubmitting}
                    >
                        {getCountries().map((countryCode) => (
                          <option key={countryCode} value={getCountryCallingCode(countryCode)}>
                            {en[countryCode]} (+{getCountryCallingCode(countryCode)})
                          </option>
                        ))}
                    </select>
                    <span className="arrow-down"></span>
                  </div>
                </div>
                <div className="phone-input">
                  <div className="input-container">
                    <input 
                      type="tel" 
                      name="phoneNo"
                      id="phoneNo" 
                      value={formData.phoneNo}
                      onChange={handleInputChange}
                      onFocus={() => handleFocus('phoneNo')}
                      onBlur={handleBlur}
                      placeholder={focusedField === 'phoneNo' ? '' : ''}
                      disabled={isSubmitting}
                    />
                    <label htmlFor="phoneNo">Phone no.<span className="required">*</span></label>
                  </div>
                </div>
              </div>
              <div className="form-group">
                <div className="input-container">
                  <textarea 
                    name="message"
                    id="message" 
                    value={formData.message}
                    onChange={handleInputChange}
                    onFocus={() => handleFocus('message')}
                    onBlur={handleBlur}
                    placeholder={focusedField === 'message' ? '' : 'Your message here...'}
                    disabled={isSubmitting}
                  ></textarea>
                 
                </div>
              </div>
              <button 
                type="submit" 
                className={`send-message-btn ${isSubmitting ? 'submitting' : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send message'}
              </button>
            </form>
          </div>
        </div>
      </section>
      
    </div>
  );
};

export default ContactUs; 